import { apiClient } from "@/lib/api";
import i18n from "@/lib/i18n";
import { parseContentDispositionFilename } from "@/lib/contentDisposition";
import type {
  BatchAddOrdersResult,
  BuildRoutePayload,
  BuildRouteResultResponse,
  BuildRouteTaskResponse,
  CreateRoutePayload,
  MoveStopPayload,
  MoveStopResponse,
  PaginatedResponse,
  PaginatedRoutes,
  RouteDetail,
  RouteLoadCheckStatus,
  RouteStop,
  UnassignedOrder,
  UpdateRouteStopPayload,
} from "@/types/routes";
import type {
  RouteFactsResponse,
  RouteFactsResult,
  RouteStopWithFact,
} from "@/types/tracking";

export interface GetRoutesParams {
  date?: string;
  date__gte?: string;
  date__lte?: string;
  /**
   * Route status filter. Accepts a single status string or an array of
   * statuses. When an array is supplied, axios serializes it as repeated
   * `?status=a&status=b` params, which the backend `RouteFilter.status`
   * (`BaseInFilter`, lookup `in`) resolves to the UNION of the requested
   * statuses (NOT comma-CSV). An empty/omitted value means "all".
   */
  status?: string[] | string;
  search?: string;
  page?: number;
  page_size?: number;
  /**
   * One or more vehicle ids. TASK-4901 widened this from a single number to
   * accept an array, sent as REPEATED params (`?vehicle_id=3&vehicle_id=7`) —
   * the backend's `IdInFilter` reads `getlist()`, so a CSV would silently keep
   * only the LAST value while every single-value test still passed.
   * Values WITHIN this filter are OR-ed.
   */
  vehicle_id?: number | number[];
  /**
   * One or more driver ids, same repeated-param encoding.
   *
   * Combined with `vehicle_id` this is an **AND / intersection** (owner
   * decision 1, 2026-08-26): driver Иван + vehicle Ford Transit #1 yields
   * routes on that vehicle driven by Иван — possibly zero. It is NOT a union.
   *
   * The driver is DERIVED from `vehicle.driver`; a route with no vehicle has no
   * driver and matches neither this filter nor `vehicle_id`.
   */
  driver_id?: number | number[];
  /**
   * One or more START-depot warehouse ids (Sprint 226, TASK-5139), same repeated-param
   * encoding as `vehicle_id` (backend `IdInFilter`); values OR-ed.
   */
  warehouse_id?: number | number[];
}

/**
 * Fetches a paginated list of routes.
 * GET /api/v1/routes/
 *
 * @param params - Optional query parameters: date (YYYY-MM-DD), status, vehicle_id, page
 */
export async function getRoutes(
  params?: GetRoutesParams,
): Promise<PaginatedRoutes> {
  const response = await apiClient.get<PaginatedRoutes>("/v1/routes/", {
    params,
    // Redundant with the global apiClient paramsSerializer, kept explicit here:
    // `indexes: null` serializes an array param as repeated `?status=a&status=b`
    // (no `[]` suffix), matching the backend `MultipleChoiceFilter` +
    // `getlist("status")` contract.
    paramsSerializer: { indexes: null },
  });
  return response.data;
}

/**
 * Fetches a single route by ID.
 * GET /api/v1/routes/{id}/
 */
export async function getRoute(id: number): Promise<RouteDetail> {
  const response = await apiClient.get<RouteDetail>(`/v1/routes/${id}/`);
  return response.data;
}

/**
 * Fetches the pre-route depot load-check status for a route.
 * GET /api/v1/routes/{id}/load-check/
 *
 * Returns required vs scanned parcel-code counts, the tenant strictness mode
 * (off/warn/strict), the flagged flag, and a per-order unscanned breakdown.
 */
export async function getRouteLoadCheck(
  id: number,
): Promise<RouteLoadCheckStatus> {
  const response = await apiClient.get<RouteLoadCheckStatus>(
    `/v1/routes/${id}/load-check/`,
  );
  return response.data;
}

/**
 * Creates a new route.
 * POST /api/v1/routes/
 */
export async function createRoute(
  data: CreateRoutePayload,
): Promise<RouteDetail> {
  const response = await apiClient.post<RouteDetail>("/v1/routes/", data);
  return response.data;
}

/**
 * Partially updates an existing route.
 * PATCH /api/v1/routes/{id}/
 */
export async function updateRoute(
  id: number,
  data: Partial<CreateRoutePayload> & { visit_order?: "strict" | "any" },
): Promise<RouteDetail> {
  const response = await apiClient.patch<RouteDetail>(
    `/v1/routes/${id}/`,
    data,
  );
  return response.data;
}

/**
 * Deletes a route.
 * DELETE /api/v1/routes/{id}/
 */
export async function deleteRoute(id: number): Promise<void> {
  await apiClient.delete(`/v1/routes/${id}/`);
}

/**
 * Disbands (cancels) a route: moves it to the terminal `cancelled` status,
 * detaches its stops, and returns its orders to the unassigned pool. The route
 * row is NOT deleted.
 * POST /api/v1/routes/{id}/disband/
 *
 * Returns the serialized route (200). Rejects with 409 when the route is already
 * cancelled/completed/archived, and 403 for a driver.
 */
export async function disbandRoute(id: number): Promise<RouteDetail> {
  const response = await apiClient.post<RouteDetail>(
    `/v1/routes/${id}/disband/`,
  );
  return response.data;
}

/**
 * Releases a route to its driver — the dispatcher's "this plan is final, send it".
 * POST /api/v1/routes/{id}/release/
 *
 * Until a route is released it is invisible in the driver's mobile app, so stops can be added,
 * reordered and removed without the driver watching the plan change under them (TASK-4736). The
 * driver still STARTS the route themselves, which keeps the recorded departure time real.
 *
 * Idempotent — releasing twice keeps the original timestamp. 409 when the route has no vehicle
 * (nobody to send it to) or is no longer `planned`.
 */
export async function releaseRoute(id: number): Promise<RouteDetail> {
  const response = await apiClient.post<RouteDetail>(
    `/v1/routes/${id}/release/`,
  );
  return response.data;
}

export interface ReleaseBatchResult {
  released: number[];
  skipped: Array<{ id: number; reason: string }>;
}

/**
 * Releases several routes at once — the morning send.
 * POST /api/v1/routes/release-batch/
 *
 * Reports each route individually rather than failing the batch: one route missing a vehicle must
 * not stop the rest from reaching their drivers.
 */
export async function releaseRoutes(
  routeIds: number[],
): Promise<ReleaseBatchResult> {
  const response = await apiClient.post<ReleaseBatchResult>(
    "/v1/routes/release-batch/",
    { route_ids: routeIds },
  );
  return response.data;
}

/**
 * Result of a re-optimization. `dropped_count` (BUG-407) is how many stops the
 * solver could not place — those orders are returned to the unassigned pool
 * (status=NEW, route=null) by the backend, so the frontend can toast the count.
 * The field is optional so older/other response shapes stay compatible.
 */
export interface ReoptimizeResult {
  dropped_count?: number;
  /**
   * BUG-446: false when the solver abandoned the run and the route was left exactly as it stood
   * (it could not place a single stop). `dropped_count === 0` cannot express this — that is also
   * what a SUCCESSFUL re-optimization returns, so an abandoned run was indistinguishable from a
   * clean one and the dispatcher was told nothing. Optional for the same compatibility reason.
   */
  reoptimized?: boolean;
}

/**
 * Re-runs OR-Tools on a route's current stops and updates positions/ETAs.
 * POST /api/v1/routes/{id}/reoptimize/
 */
export async function reoptimizeRoute(id: number): Promise<ReoptimizeResult> {
  const { data } = await apiClient.post<ReoptimizeResult>(
    `/v1/routes/${id}/reoptimize/`,
  );
  return data ?? {};
}

/**
 * Transitions a route to a new status.
 * POST /api/v1/routes/{id}/status/
 */
export async function transitionRouteStatus(
  id: number,
  action: "start" | "complete" | "archive",
): Promise<RouteDetail> {
  const response = await apiClient.post<RouteDetail>(
    `/v1/routes/${id}/status/`,
    { action },
  );
  return response.data;
}

/**
 * Fetches all stops for a route.
 * GET /api/v1/routes/{id}/stops/
 */
export async function getRouteStops(routeId: number): Promise<RouteStop[]> {
  const response = await apiClient.get<RouteStop[]>(
    `/v1/routes/${routeId}/stops/`,
  );
  return response.data;
}

/**
 * Partially updates a single route stop.
 * PATCH /api/v1/routes/{routeId}/stops/{stopId}/
 */
export async function updateRouteStop(
  routeId: number,
  stopId: number,
  data: UpdateRouteStopPayload,
): Promise<RouteStop> {
  const response = await apiClient.patch<RouteStop>(
    `/v1/routes/${routeId}/stops/${stopId}/`,
    data,
  );
  return response.data;
}

/**
 * Reorders stops on a route by supplying new position values.
 * PUT /api/v1/routes/{routeId}/stops/reorder/
 */
export async function reorderRouteStops(
  routeId: number,
  positions: Array<{ id: number; position: number }>,
): Promise<RouteStop[]> {
  const response = await apiClient.put<RouteStop[]>(
    `/v1/routes/${routeId}/stops/reorder/`,
    positions,
  );
  return response.data;
}

/**
 * Adds an order as a stop on a route.
 * POST /api/v1/routes/{routeId}/add_order/
 */
export async function addOrderToRoute(
  routeId: number,
  orderId: number,
  options?: {
    /**
     * BUG-453: the dispatcher's explicit "yes, I know" for an OVERWEIGHT route. Weight is a
     * regulatory limit, so an overload is their call. It does NOT unblock a volume refusal —
     * that one is physical and the backend ignores this flag for it.
     */
    allowOverweight?: boolean;
  },
): Promise<RouteStop> {
  const response = await apiClient.post<RouteStop>(
    `/v1/routes/${routeId}/add_order/`,
    {
      order_id: orderId,
      ...(options?.allowOverweight ? { allow_overweight: true } : {}),
    },
  );
  return response.data;
}

/**
 * Adds multiple orders as stops on a route in one request.
 * POST /api/v1/routes/{routeId}/batch_add_orders/
 *
 * `templateIds` (BUG-668) are PERMANENT orders — `OrderTemplate` pks, a different
 * id space from `orderIds` (order 12 and template 12 are different objects). The
 * backend materializes each template into a working order dated from the route
 * and attaches it; the template itself stays reusable. Sent only when non-empty,
 * so every existing caller's request body is unchanged.
 */
export async function batchAddOrdersToRoute(
  routeId: number,
  orderIds: number[],
  templateIds?: number[],
): Promise<RouteStop[]> {
  const response = await apiClient.post<RouteStop[]>(
    `/v1/routes/${routeId}/batch_add_orders/`,
    batchAddBody(orderIds, templateIds),
  );
  return response.data;
}

/**
 * Same call as {@link batchAddOrdersToRoute}, opted in to `include_unplaced`
 * (BUG-668): the 200 body becomes `{stops, unplaced}`, naming every order the
 * solver refused and why, instead of dropping it silently.
 *
 * A separate function rather than a flag on the original so each keeps one
 * honest return type; callers that do not report refusals are untouched.
 * When EVERY new order is refused the backend still answers 409
 * `ROUTE_ORDER_UNPLACEABLE` with the same items in `extra_data.unplaced`.
 */
export async function batchAddOrdersToRouteWithUnplaced(
  routeId: number,
  orderIds: number[],
  templateIds?: number[],
): Promise<BatchAddOrdersResult> {
  const response = await apiClient.post<BatchAddOrdersResult>(
    `/v1/routes/${routeId}/batch_add_orders/`,
    { ...batchAddBody(orderIds, templateIds), include_unplaced: true },
  );
  return {
    stops: response.data.stops ?? [],
    unplaced: response.data.unplaced ?? [],
  };
}

/** `template_ids` is sent only when non-empty, so ordinary-only bodies stay unchanged. */
function batchAddBody(orderIds: number[], templateIds?: number[]) {
  return {
    order_ids: orderIds,
    ...(templateIds && templateIds.length > 0
      ? { template_ids: templateIds }
      : {}),
  };
}

/**
 * Removes an order stop from a route.
 * DELETE /api/v1/routes/{routeId}/remove_order/{orderId}/
 */
export async function removeOrderFromRoute(
  routeId: number,
  orderId: number,
): Promise<void> {
  await apiClient.delete(`/v1/routes/${routeId}/remove_order/${orderId}/`);
}

/**
 * Exports a route as a PDF file.
 * GET /api/v1/routes/{id}/export/pdf/
 *
 * tracker#44: the sheet's field labels are rendered server-side in the dispatcher's interface
 * language, so the request has to carry which one that is. `Accept-Language` is sent
 * EXPLICITLY from the i18n instance rather than left to the browser's own header: the browser
 * reports the language of the OS/browser install, which is a different thing from the language
 * the dispatcher picked in the app. An English Windows with a Russian interface would otherwise
 * print an English route sheet.
 *
 * Set per-request rather than on the shared `apiClient` interceptor on purpose — this is the
 * only endpoint that reads the header (the rest of the API follows DEV_RULES §6.4: the server
 * returns a code, the client translates), and a header attached to all ~200 endpoints is a
 * change nobody can see the blast radius of.
 */
export async function exportRoutePdf(routeId: number): Promise<Blob> {
  const response = await apiClient.get(`/v1/routes/${routeId}/export/pdf/`, {
    responseType: "blob",
    headers: { "Accept-Language": i18n.language || "ru" },
  });
  return response.data as Blob;
}

/**
 * Fetches plan vs fact comparison data for all stops on a route.
 * GET /api/v1/routes/{id}/facts/
 */
export async function getRouteFacts(
  routeId: number,
): Promise<RouteFactsResult> {
  const response = await apiClient.get<
    | RouteFactsResponse
    | RouteStopWithFact[]
    | PaginatedResponse<RouteStopWithFact>
  >(`/v1/routes/${routeId}/facts/`);
  const data = response.data;

  // The backend returns a dict with a `stops` array and a `tracker_linked`
  // flag: { route_id, stops: [...], tracker_linked }.
  // Read `.stops` first. Defensively accept a bare array and a DRF paginated
  // envelope ({ count, results }) as fallbacks so this can never throw, and
  // always return a normalized RouteFactsResult.
  if (Array.isArray(data)) {
    return { stops: data, trackerLinked: false };
  }
  if (data && Array.isArray((data as RouteFactsResponse).stops)) {
    return {
      stops: (data as RouteFactsResponse).stops,
      trackerLinked: (data as RouteFactsResponse).tracker_linked === true,
    };
  }
  if (
    data &&
    Array.isArray((data as PaginatedResponse<RouteStopWithFact>).results)
  ) {
    return {
      stops: (data as PaginatedResponse<RouteStopWithFact>).results,
      trackerLinked: false,
    };
  }
  return { stops: [], trackerLinked: false };
}

/**
 * Starts an async route build from a list of order IDs using the specified strategy.
 * POST /api/v1/routes/build/ → HTTP 202
 *
 * Returns a task acknowledgement with task_id and status="pending".
 * Poll getBuildRouteResult(task_id) every 2 seconds until status is
 * "completed" or "failed".
 */
export async function buildRoute(
  payload: BuildRoutePayload,
): Promise<BuildRouteTaskResponse> {
  const response = await apiClient.post<BuildRouteTaskResponse>(
    "/v1/routes/build/",
    payload,
  );
  return response.data;
}

/**
 * Polls the result of an async route build task.
 * GET /api/v1/routes/build/result/{taskId}/
 *
 * Returns pending/running while the Celery task is in progress,
 * completed (with result.routes) on success, or failed (with error) on failure.
 */
export async function getBuildRouteResult(
  taskId: string,
): Promise<BuildRouteResultResponse> {
  const response = await apiClient.get<BuildRouteResultResponse>(
    `/v1/routes/build/result/${taskId}/`,
  );
  return response.data;
}

export interface GetUnassignedOrdersParams {
  /** Single delivery date (YYYY-MM-DD). Mutually exclusive with date__gte/date__lte. */
  date?: string;
  /** Start of delivery-date range (YYYY-MM-DD). */
  date__gte?: string;
  /** End of delivery-date range (YYYY-MM-DD). */
  date__lte?: string;
}

/**
 * Fetches orders with no RouteStop on any route for the given date or date range.
 * GET /api/v1/routes/unassigned-orders/?date=YYYY-MM-DD
 * GET /api/v1/routes/unassigned-orders/?date__gte=YYYY-MM-DD&date__lte=YYYY-MM-DD
 */
export async function getUnassignedOrders(
  params: string | GetUnassignedOrdersParams,
): Promise<UnassignedOrder[]> {
  // Accept legacy single-string form (backwards compat) or new params object.
  const queryParams: GetUnassignedOrdersParams =
    typeof params === "string" ? { date: params } : params;
  const response = await apiClient.get<UnassignedOrder[]>(
    "/v1/routes/unassigned-orders/",
    { params: queryParams },
  );
  return response.data;
}

/**
 * Returns the task_id of the currently active route-build task for this tenant,
 * or null if no task is in progress.
 * GET /api/v1/routes/build/active/
 */
export async function getActiveBuildTask(): Promise<{
  task_id: string | null;
}> {
  const { data } = await apiClient.get<{ task_id: string | null }>(
    "/v1/routes/build/active/",
  );
  return data;
}

/**
 * Moves a stop from one route to another (or assigns an unassigned order to a route).
 * POST /api/v1/routes/move-stop/
 */
export async function moveStop(
  payload: MoveStopPayload,
): Promise<MoveStopResponse> {
  const { data } = await apiClient.post<MoveStopResponse>(
    "/v1/routes/move-stop/",
    payload,
  );
  return data;
}

/** Printable label layout: A4 multi-up sheet or single thermal 10x15 page. */
export type LabelFormat = "a4" | "thermal";

/**
 * Downloads the printable parcel-label PDF for all coded parcels on a route.
 * GET /api/v1/routes/{id}/labels.pdf?format=a4|thermal
 *
 * Returns the PDF blob together with the filename suggested by the
 * `Content-Disposition` header (falls back to `route-<id>-labels-<format>.pdf`).
 * Surfaces backend errors verbatim to the caller (409 when `parcels_enabled` is
 * false, 404 cross-tenant, 403 driver), which must be handled with a toast.
 */
export async function getRouteLabelsPdf(
  routeId: number,
  format: LabelFormat,
): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>(
    `/v1/routes/${routeId}/labels.pdf`,
    { params: { format }, responseType: "blob" },
  );
  const disposition = response.headers["content-disposition"];
  const filename =
    parseContentDispositionFilename(
      typeof disposition === "string" ? disposition : undefined,
    ) ?? `route-${routeId}-labels-${format}.pdf`;
  return { blob: response.data, filename };
}

/**
 * Sets or clears the dispatcher's one-off «придержать» on ONE route.
 * PATCH /api/v1/routes/{id}/
 *
 * A hold makes auto-release skip this route on this date. It is a decision about
 * ONE date and leaves the schedule's standing rule untouched — which is what
 * makes it different in kind from `RouteSchedule.auto_release === false`, where
 * the schedule has no auto-send at all.
 *
 * **`Route.release_hold` has SHIPPED** with TASK-4924 (`routes/models.py:165`,
 * migration `0023`), and this PATCH persists. Verified against the live dev API
 * on 2026-09-09: PATCH `{release_hold: true}` returns `true` and the re-GET
 * still reads `true`.
 *
 * This comment previously said the field did not exist and that "the toggle
 * will not persist" — true when written, stale once the backend landed, and
 * actively misleading: it would send a reader hunting a persistence bug that is
 * not there. Kept as a NAMED function rather than a bare `updateRoute` call so
 * there is exactly one place that owns the field name.
 */
export async function setRouteReleaseHold(
  id: number,
  releaseHold: boolean,
): Promise<RouteDetail> {
  const response = await apiClient.patch<RouteDetail>(`/v1/routes/${id}/`, {
    release_hold: releaseHold,
  });
  return response.data;
}

/**
 * Substitutes the driver and/or vehicle on ONE already-materialised route.
 * PATCH /api/v1/routes/{id}/
 *
 * **Writes route-level fields ONLY.** `Vehicle.driver` — the vehicle's STANDING
 * driver — is never touched by this call, and that is the entire point of the
 * feature: writing it would move every route that vehicle has ever run,
 * including past billed ones carrying delivery confirmations, under the
 * substitute. The permitted keys are pinned in `SUBSTITUTION_PAYLOAD_KEYS` and
 * asserted against this payload in the tests.
 *
 * A `null` clears the override and returns the route to the standing driver.
 * The backend refuses a non-driver with `ROUTE_DRIVER_NOT_ELIGIBLE`; the UI
 * offers only tenant users with the `driver` role, so that refusal is a backstop
 * rather than something a dispatcher should ever meet.
 */
export async function substituteRouteAssignment(
  id: number,
  values: { driver?: number | null; vehicle?: number | null },
): Promise<RouteDetail> {
  const response = await apiClient.patch<RouteDetail>(
    `/v1/routes/${id}/`,
    values,
  );
  return response.data;
}
